/**
 * Shape commune des registries de colonnes par page (Suivi ELS, Planning
 * Photos, Suivi Publication, Apartments for Rent). Demande user 2026-05-08 :
 * un admin simulant CLEVEL pilote la sélection visible pour TOUS les rôles
 * non-admin via une row globale par page (table `page_columns_config`).
 *
 * Note : `biensColumns.ts` reste son propre registry (per-user picker
 * historique, pattern différent) — pas de tentative de fusion V1.
 */

export interface ColumnDef<K extends string> {
  /** Identifiant stable (persisté en DB JSONB). */
  key: K;
  /** Clé i18n du label de l'en-tête (ex: "appartsForRent.refInterne"). */
  i18nLabel: string;
  /** Alignement de la cellule. */
  align: "left" | "right" | "center";
  /** Affichée par défaut tant que l'admin n'a pas configuré la page. */
  defaultVisible: boolean;
  /** Largeur fixe de la colonne en px (optionnel — pour les tables à largeurs figées). */
  width?: number;
}

/**
 * Filtre + réordonne le registry selon la sélection user. Ignore silencieusement
 * les ids inconnus (registry évolue → orphelins en DB pas bloquants).
 * Préserve l'ordre demandé par `visible` (pas l'ordre du registry).
 */
export function getOrderedVisibleColumns<K extends string>(
  registry: ColumnDef<K>[],
  visible: K[],
): ColumnDef<K>[] {
  const byKey = new Map(registry.map((c) => [c.key, c]));
  const out: ColumnDef<K>[] = [];
  for (const k of visible) {
    const def = byKey.get(k);
    if (def) out.push(def);
  }
  return out;
}

/** Colonnes affichées par défaut quand aucune config n'a jamais été sauvée. */
export function defaultVisibleKeys<K extends string>(registry: ColumnDef<K>[]): K[] {
  return registry.filter((c) => c.defaultVisible).map((c) => c.key);
}

/** Filtre une sélection user pour ne garder que les keys présentes dans le registry. */
export function sanitizeVisibleKeys<K extends string>(
  registry: ColumnDef<K>[],
  candidate: unknown,
): K[] | null {
  if (!Array.isArray(candidate)) return null;
  const known = new Set(registry.map((c) => c.key));
  const out: K[] = [];
  for (const v of candidate) {
    if (typeof v === "string" && known.has(v as K)) out.push(v as K);
  }
  return out;
}

/** Helper pour mapper align → classe Tailwind cellule. */
export function alignClass(align: ColumnDef<string>["align"]): string {
  return align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
}
